import React, {useEffect, useState} from "react";
import {ReloadOutlined} from "@ant-design/icons";
import {Button, Space, Spin, Tooltip, Typography} from "antd";
import {useTranslation} from "react-i18next";
import {buildAuthorizedFileFetchOptions} from "../../../fileDownloadUrl";

type Props = {
    eventId: number;
    participantId: number;
    codeId: number;
    authToken?: string;
    size?: number;
};

export default function ParticipantInvitationQrImage({eventId, participantId, codeId, authToken, size = 192}: Props) {
    const {t} = useTranslation();
    const [imageUrl, setImageUrl] = useState<string>();
    const [status, setStatus] = useState<"loading" | "ready" | "unavailable" | "error">("loading");
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        let objectUrl: string | undefined;
        setImageUrl(undefined);
        setStatus("loading");

        const load = async () => {
            try {
                const response = await fetch(
                    `/api/events/${eventId}/participants/${participantId}/registration-codes/${codeId}/qr`,
                    {...buildAuthorizedFileFetchOptions(authToken), cache: "no-store", signal: controller.signal}
                );
                if (controller.signal.aborted) return;
                if (response.status === 404) {
                    setStatus("unavailable");
                    return;
                }
                if (!response.ok) throw new Error("QR image request failed");
                const blob = await response.blob();
                if (controller.signal.aborted) return;
                if (blob.type !== "image/png" || !blob.size) throw new Error("Invalid QR image");
                objectUrl = URL.createObjectURL(blob);
                setImageUrl(objectUrl);
                setStatus("ready");
            } catch {
                if (!controller.signal.aborted) setStatus("error");
            }
        };
        void load();

        return () => {
            controller.abort();
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [eventId, participantId, codeId, authToken, attempt]);

    if (status === "unavailable") {
        return <Typography.Text type="secondary">{t("qr-image-unavailable")}</Typography.Text>;
    }

    if (status === "error") {
        return (
            <Space wrap>
                <Typography.Text type="danger">{t("qr-image-load-failed")}</Typography.Text>
                <Tooltip title={t("qr-image-retry")}>
                    <Button icon={<ReloadOutlined/>} aria-label={t("qr-image-retry")} onClick={() => setAttempt(value => value + 1)}/>
                </Tooltip>
            </Space>
        );
    }

    return (
        <div style={{width: size, maxWidth: "100%", aspectRatio: "1", display: "grid", placeItems: "center", background: "#fff"}}>
            {status === "loading" ? <Spin aria-label={t("qr-image-loading")}/> : (
                <img src={imageUrl} alt={t("qr-invitation-image")} width={size} height={size}
                     style={{display: "block", width: "100%", height: "auto"}} onError={() => setStatus("error")}/>
            )}
        </div>
    );
}
